import {
  IconName,
  NavigationItemProps,
  NavigationItemWithChildrenProps,
} from '@taxbit-private/cosmic'
import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { ContactsTab } from 'pages/contacts/helpers/types'
import { getTransactionsQueryString } from 'pages/transactions/util/transactionsQueryString'
import { SettingsPath } from 'routes/paths'

import Permission from 'constants/Permissions'
import { useUser } from 'contexts/UserContext'
import useFeatureFlags from 'hooks/feature-flags/useFeatureFlags'
import { getIsAccountingPageAccessible } from 'hooks/utils/isAccountingPageAccessible'
import useIsLegalEntityRootOrg from 'hooks/utils/useIsLegalEntityRootOrg'
import useIsVoucherEnabled from 'hooks/utils/useIsVoucherEnabled'
import useIsZeusAdminOrg from 'hooks/utils/useIsZeusAdminOrg'
import { isZeusEnvironment } from 'utils/environment'
import isEmpty from 'utils/isEmpty'

import { useDynamicLabels } from './useDynamicLabels'
import { useIsTopLevelOrg } from './useIsTopLevelOrg'

const PAYMENTS_URL = 'https://payments.taxbit.com'

enum LeftRailTrackingId {
  Dashboard = 'dashboard-nav-item',
  Transactions = 'transactions-nav-item',
  Reports = 'reports-nav-item',
  ReportsGeneral = 'reports-general-nav-item',
  ReportsEntries = 'reports-entries-nav-item',
  ReportsDisclosures = 'reports-disclosures-nav-item',
  ReportsQa = 'reports-qa-nav-item',
  Sources = 'sources-nav-item',
  Contacts = 'contacts-nav-item',
  Accounting = 'accounting-nav-item',
  Payments = 'payments-nav-item',
  Rules = 'rules-nav-item',
  Settings = 'settings-nav-item',
  SettingsOverview = 'settings-overview-nav-item',
  SettingsPricing = 'settings-pricing-nav-item',
  SettingsAccounting = 'settings-accounting-nav-item',
  SettingsTax = 'settings-tax-nav-item',
  SettingsTeam = 'settings-team-nav-item',
  SettingsSpamTokens = 'settings-spam-tokens-nav-item',
  SettingsStableCoins = 'settings-stable-coins-nav-item',
  SettingsCategories = 'settings-categories-nav-item',
  SuperAdmin = 'super-admin-nav-item',
  SuperAdminOrgControls = 'super-admin-org-controls-nav-item',
  SuperAdminRequests = 'super-admin-requests-nav-item',
  SuperAdminEntities = 'super-admin-entities-nav-item',
  SuperAdminOrganization = 'super-admin-organization-nav-item',
  SuperAdminCustomFields = 'super-admin-custom-fields-nav-item',
  SuperAdminImposterAllowlist = 'super-admin-imposter-allowlist-nav-item',
  SuperAdminIngestionJobs = 'super-admin-ingestion-jobs-nav-item',
  PrincipalMarketAnalysis = 'principal-market-analysis-nav-item',
  Cases = 'cases-nav-item',
  SettingsAuditLog = 'settings-audit-log-nav-item',
  Vouchers = 'vouchers-nav-item',
  ZeusItAdmin = 'zeus-it-admin-nav-item',
  ZeusItAdminEntities = 'zeus-it-admin-entities-nav-item',
}

export enum ListRoutes {
  Dashboard = '/app/dashboard',
  Transactions = '/app/transactions',
  ReportsFlattened = '/app/reports',
  Reports = '/app/reports/:reportType',
  ReportDetails = '/app/reports/:reportType/:reportId',
  Sources = '/app/sources',
  Contacts = '/app/contacts',
  Accounting = '/app/accounting',
  Rules = '/app/rules',
  Settings = '/app/settings/:section',
  PrincipalMarketAnalysis = '/app/pma',
  Cases = '/app/cases',
  Vouchers = '/app/vouchers',
}

export const replaceParams = (route: string, params: Record<string, string>): string => {
  return Object.entries(params).reduce(
    (updatedRoute, [key, value]) => updatedRoute.replace(`:${key}`, value),
    route
  )
}

export const useLeftRailItems = () => {
  const {
    shouldShowPricingSettings,
    isStableCoinsEnabled,
    isPrincipalMarketAnalysisEnabled,
    isPrincipalMarketAnalysisVisible,
    showContactsPageForGcOrg,
    showRulesPageForGcOrg,
    isSourcesRouteHidden,
    showCategoriesSettings,
    enableGrandCanyonErpSync,
    enableCustomFields,
    isPublicSector,
    isAuditLogEnabled,
    shouldShowPaymentsNavItem,
    isMultiBookEnabled,
    isAccountingPageVisible,
    isCustomErpMappingsEnabled,
  } = useFeatureFlags()

  const location = useLocation()
  const { user, hasPermission } = useUser()

  const { isParentOrg, isLoading: isParentOrgLoading } = useIsTopLevelOrg()

  const { isLegalEntityRootOrg } = useIsLegalEntityRootOrg()
  const { isVoucherEnabled } = useIsVoucherEnabled()

  const { contactsLabel, accountingLabel, legalEntitiesLabel, taxLabel } = useDynamicLabels()

  const shouldShowSourcesPage = !isSourcesRouteHidden && !isLegalEntityRootOrg

  const shouldShowDashboard = !isPublicSector || (!isParentOrgLoading && !isParentOrg)

  // The Zeus operational org (VITE_AUTH_ADMIN_ORG === 'Zeus') is a login/superadmin
  // holding org, not an accounting entity, so its nav is stripped to a minimal set.
  const isZeusAdminOrg = useIsZeusAdminOrg()

  const handleTransactionsLink = (clickable: JSX.Element) => {
    const queryString = getTransactionsQueryString()

    const url = !isEmpty(queryString)
      ? `${ListRoutes.Transactions}?${queryString}`
      : ListRoutes.Transactions

    return <Link to={url}>{clickable}</Link>
  }

  return useMemo<Array<NavigationItemProps | NavigationItemWithChildrenProps>>(
    () => [
      ...(shouldShowDashboard &&
      !isZeusAdminOrg &&
      hasPermission(Permission.TransactionDashboardRead)
        ? [
            {
              label: 'Dashboard',
              iconName: 'pie-chart' as IconName,
              trackingId: LeftRailTrackingId.Dashboard,
              isActive: location.pathname.includes(ListRoutes.Dashboard),
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={ListRoutes.Dashboard}>{clickable}</Link>
              ),
            },
          ]
        : []),
      ...(!isZeusAdminOrg && hasPermission(Permission.TransactionDataRead)
        ? [
            {
              label: 'Transactions',
              iconName: 'table' as IconName,
              trackingId: LeftRailTrackingId.Transactions,
              isActive: location.pathname.includes(ListRoutes.Transactions),
              renderWrapper: (clickable: JSX.Element) => handleTransactionsLink(clickable),
            },
          ]
        : []),
      ...(!isZeusAdminOrg && hasPermission(Permission.AccountingReportRead)
        ? [
            {
              label: 'Reports',
              iconName: 'file-text' as IconName,
              trackingId: LeftRailTrackingId.Reports,
              isActive: location.pathname.includes(ListRoutes.ReportsFlattened),
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={ListRoutes.ReportsFlattened}>{clickable}</Link>
              ),
            },
          ]
        : []),
      ...(shouldShowSourcesPage &&
      !isZeusAdminOrg &&
      hasPermission(Permission.TransactionSourceRead)
        ? [
            {
              label: 'Sources',
              iconName: 'database' as IconName,
              isActive: location.pathname.includes(ListRoutes.Sources),
              trackingId: LeftRailTrackingId.Sources,
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={ListRoutes.Sources}>{clickable}</Link>
              ),
            },
          ]
        : []),
      ...(isPublicSector && isParentOrg
        ? [
            {
              label: 'Cases',
              iconName: 'users' as IconName,
              isActive: location.pathname.includes(ListRoutes.Cases),
              trackingId: LeftRailTrackingId.Cases,
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={ListRoutes.Cases}>{clickable}</Link>
              ),
            },
          ]
        : []),
      ...(showContactsPageForGcOrg && hasPermission(Permission.SettingContactRead)
        ? [
            {
              label: contactsLabel,
              iconName: 'users' as IconName,
              isActive: location.pathname.includes(ListRoutes.Contacts),
              trackingId: LeftRailTrackingId.Contacts,
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={`${ListRoutes.Contacts}/${ContactsTab.Saved}`}>{clickable}</Link>
              ),
            },
          ]
        : []),
      ...(getIsAccountingPageAccessible({
        isAccountingPageVisible,
        isCustomErpMappingsEnabled,
        enableGrandCanyonErpSync,
        isPublicSector,
      }) && hasPermission(Permission.AccountingErpRead)
        ? [
            {
              label: 'Accounting',
              iconName: 'book-open' as IconName,
              isActive: location.pathname.includes(ListRoutes.Accounting),
              trackingId: LeftRailTrackingId.Accounting,
              renderWrapper: (clickable: React.ReactNode) => (
                <Link to={ListRoutes.Accounting}>{clickable}</Link>
              ),
            },
          ]
        : []),
      ...(shouldShowPaymentsNavItem
        ? [
            {
              label: 'Payments',
              iconName: 'globe' as IconName,
              isActive: false,
              trackingId: LeftRailTrackingId.Payments,
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={PAYMENTS_URL} target="_blank" rel="noopener noreferrer">
                  {clickable}
                </Link>
              ),
            },
          ]
        : []),
      ...(showRulesPageForGcOrg && hasPermission(Permission.TransactionRuleRead)
        ? [
            {
              label: 'Rules',
              iconName: 'shuffle' as IconName,
              isActive: location.pathname.includes(ListRoutes.Rules),
              trackingId: LeftRailTrackingId.Rules,
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={ListRoutes.Rules}>{clickable}</Link>
              ),
            },
          ]
        : []),
      ...(isPrincipalMarketAnalysisEnabled &&
      isPrincipalMarketAnalysisVisible &&
      hasPermission(Permission.PrincipalMarketAnalysisRead)
        ? [
            {
              label: 'Principal Market Analysis',
              iconName: 'bar-chart' as IconName,
              isActive: location.pathname.includes(ListRoutes.PrincipalMarketAnalysis),
              trackingId: LeftRailTrackingId.PrincipalMarketAnalysis,
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={ListRoutes.PrincipalMarketAnalysis}>{clickable}</Link>
              ),
            },
          ]
        : []),
      ...(isVoucherEnabled && !isZeusAdminOrg && hasPermission(Permission.VoucherView)
        ? [
            {
              label: 'Vouchers',
              iconName: 'file-text' as IconName,
              isActive: location.pathname.includes(ListRoutes.Vouchers),
              trackingId: LeftRailTrackingId.Vouchers,
              renderWrapper: (clickable: JSX.Element) => (
                <Link to={ListRoutes.Vouchers}>{clickable}</Link>
              ),
            },
          ]
        : []),
      {
        label: 'Settings',
        iconName: 'settings',
        trackingId: LeftRailTrackingId.Settings,
        children: [
          ...(!user?.isZeusITAdmin && !isZeusAdminOrg && hasPermission(Permission.SettingOrgRead)
            ? [
                {
                  label: 'Overview',
                  trackingId: LeftRailTrackingId.SettingsOverview,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.Overview }),
                  renderWrapper: (clickable: JSX.Element) => (
                    <Link
                      to={replaceParams(ListRoutes.Settings, { section: SettingsPath.Overview })}
                    >
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(shouldShowPricingSettings &&
          !isZeusAdminOrg &&
          hasPermission(Permission.SettingPricingRead)
            ? [
                {
                  label: 'Pricing',
                  trackingId: LeftRailTrackingId.SettingsPricing,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.Pricing }),
                  renderWrapper: (clickable: JSX.Element) => (
                    <Link
                      to={replaceParams(ListRoutes.Settings, { section: SettingsPath.Pricing })}
                    >
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(!isPublicSector && !isZeusAdminOrg && hasPermission(Permission.SettingConfigRead)
            ? [
                {
                  label: accountingLabel,
                  trackingId: LeftRailTrackingId.SettingsAccounting,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.Accounting }),
                  renderWrapper: (clickable: React.ReactNode) => (
                    <Link
                      to={replaceParams(ListRoutes.Settings, { section: SettingsPath.Accounting })}
                    >
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(isMultiBookEnabled && !isZeusAdminOrg && hasPermission(Permission.SettingConfigRead)
            ? [
                {
                  label: taxLabel,
                  trackingId: LeftRailTrackingId.SettingsTax,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.Tax }),
                  renderWrapper: (clickable: React.ReactNode) => (
                    <Link to={replaceParams(ListRoutes.Settings, { section: SettingsPath.Tax })}>
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(showCategoriesSettings &&
          !isZeusAdminOrg &&
          hasPermission(Permission.SettingCategoryRead)
            ? [
                {
                  label: 'Categories',
                  trackingId: LeftRailTrackingId.SettingsCategories,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.Categories }),
                  renderWrapper: (clickable: React.ReactNode) => (
                    <Link
                      to={replaceParams(ListRoutes.Settings, { section: SettingsPath.Categories })}
                    >
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(hasPermission(Permission.SettingTeamRead)
            ? [
                {
                  label: 'Team',
                  trackingId: LeftRailTrackingId.SettingsTeam,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.Team }),
                  renderWrapper: (clickable: JSX.Element) => (
                    <Link to={replaceParams(ListRoutes.Settings, { section: SettingsPath.Team })}>
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(!isLegalEntityRootOrg &&
          !isZeusAdminOrg &&
          hasPermission(Permission.SettingSpamTokenRead)
            ? [
                {
                  label: 'Spam Tokens',
                  trackingId: LeftRailTrackingId.SettingsSpamTokens,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.SpamTokens }),
                  renderWrapper: (clickable: JSX.Element) => (
                    <Link
                      to={replaceParams(ListRoutes.Settings, { section: SettingsPath.SpamTokens })}
                    >
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(isStableCoinsEnabled && !isZeusAdminOrg
            ? [
                {
                  label: 'Stable Coins',
                  trackingId: LeftRailTrackingId.SettingsStableCoins,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.StableCoins }),
                  renderWrapper: (clickable: JSX.Element) => (
                    <Link
                      to={replaceParams(ListRoutes.Settings, { section: SettingsPath.StableCoins })}
                    >
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
          ...(isAuditLogEnabled && hasPermission(Permission.SettingAuditLogsRead)
            ? [
                {
                  label: 'Audit Log',
                  trackingId: LeftRailTrackingId.SettingsAuditLog,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: SettingsPath.AuditLog }),
                  renderWrapper: (clickable: JSX.Element) => (
                    <Link
                      to={replaceParams(ListRoutes.Settings, { section: SettingsPath.AuditLog })}
                    >
                      {clickable}
                    </Link>
                  ),
                },
              ]
            : []),
        ],
      },
      ...(user?.isSuperAdmin
        ? [
            {
              label: 'Admin',
              iconName: 'sliders' as IconName,
              trackingId: LeftRailTrackingId.SuperAdmin,
              children: [
                ...(isZeusEnvironment()
                  ? []
                  : [
                      {
                        label: 'Org Controls',
                        trackingId: LeftRailTrackingId.SuperAdminOrgControls,
                        isActive:
                          location.pathname ===
                          replaceParams(ListRoutes.Settings, { section: 'org-controls' }),
                        renderWrapper: (clickable: React.ReactNode) => (
                          <Link
                            to={replaceParams(ListRoutes.Settings, { section: 'org-controls' })}
                          >
                            {clickable}
                          </Link>
                        ),
                      },
                    ]),
                ...(isZeusEnvironment()
                  ? []
                  : [
                      {
                        label: 'Requests',
                        trackingId: LeftRailTrackingId.SuperAdminRequests,
                        isActive:
                          location.pathname ===
                          replaceParams(ListRoutes.Settings, { section: 'requests' }),
                        renderWrapper: (clickable: React.ReactNode) => (
                          <Link to={replaceParams(ListRoutes.Settings, { section: 'requests' })}>
                            {clickable}
                          </Link>
                        ),
                      },
                    ]),
                ...(!isZeusAdminOrg && hasPermission(Permission.SettingEntityRead)
                  ? [
                      {
                        label: legalEntitiesLabel,
                        trackingId: LeftRailTrackingId.SuperAdminEntities,
                        isActive:
                          location.pathname ===
                          replaceParams(ListRoutes.Settings, { section: 'entities' }),
                        renderWrapper: (clickable: React.ReactNode) => (
                          <Link to={replaceParams(ListRoutes.Settings, { section: 'entities' })}>
                            {clickable}
                          </Link>
                        ),
                      },
                    ]
                  : []),
                {
                  label: 'Organization',
                  trackingId: LeftRailTrackingId.SuperAdminOrganization,
                  isActive:
                    location.pathname ===
                    replaceParams(ListRoutes.Settings, { section: 'organization' }),
                  renderWrapper: (clickable: React.ReactNode) => (
                    <Link to={replaceParams(ListRoutes.Settings, { section: 'organization' })}>
                      {clickable}
                    </Link>
                  ),
                },
                ...(enableCustomFields
                  ? [
                      {
                        label: 'Custom Fields',
                        trackingId: LeftRailTrackingId.SuperAdminCustomFields,
                        isActive:
                          location.pathname ===
                          replaceParams(ListRoutes.Settings, { section: 'custom-fields' }),
                        renderWrapper: (clickable: JSX.Element) => (
                          <Link
                            to={replaceParams(ListRoutes.Settings, { section: 'custom-fields' })}
                          >
                            {clickable}
                          </Link>
                        ),
                      },
                    ]
                  : []),

                ...(isZeusEnvironment()
                  ? []
                  : [
                      {
                        label: 'Imposter Allowlist',
                        trackingId: LeftRailTrackingId.SuperAdminImposterAllowlist,
                        isActive:
                          location.pathname ===
                          replaceParams(ListRoutes.Settings, { section: 'imposter-allowlist' }),
                        renderWrapper: (clickable: React.ReactNode) => (
                          <Link
                            to={replaceParams(ListRoutes.Settings, {
                              section: 'imposter-allowlist',
                            })}
                          >
                            {clickable}
                          </Link>
                        ),
                      },
                    ]),
                ...(isZeusEnvironment()
                  ? []
                  : [
                      {
                        label: 'Ingestion Jobs',
                        trackingId: LeftRailTrackingId.SuperAdminIngestionJobs,
                        isActive:
                          location.pathname ===
                          replaceParams(ListRoutes.Settings, { section: 'ingestion-jobs' }),
                        renderWrapper: (clickable: React.ReactNode) => (
                          <Link
                            to={replaceParams(ListRoutes.Settings, { section: 'ingestion-jobs' })}
                          >
                            {clickable}
                          </Link>
                        ),
                      },
                    ]),
              ],
            },
          ]
        : []),
      ...(user?.isZeusITAdmin
        ? [
            {
              label: 'Admin',
              iconName: 'sliders' as IconName,
              trackingId: LeftRailTrackingId.ZeusItAdmin,
              children: [
                ...(hasPermission(Permission.SettingEntityRead)
                  ? [
                      {
                        label: legalEntitiesLabel,
                        trackingId: LeftRailTrackingId.ZeusItAdminEntities,
                        isActive:
                          location.pathname ===
                          replaceParams(ListRoutes.Settings, { section: 'entities' }),
                        renderWrapper: (clickable: React.ReactNode) => (
                          <Link to={replaceParams(ListRoutes.Settings, { section: 'entities' })}>
                            {clickable}
                          </Link>
                        ),
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ],
    [
      shouldShowDashboard,
      isZeusAdminOrg,
      location.pathname,
      isPublicSector,
      shouldShowSourcesPage,
      isParentOrg,
      showContactsPageForGcOrg,
      contactsLabel,
      enableGrandCanyonErpSync,
      shouldShowPaymentsNavItem,
      showRulesPageForGcOrg,
      isPrincipalMarketAnalysisEnabled,
      isPrincipalMarketAnalysisVisible,
      isVoucherEnabled,
      shouldShowPricingSettings,
      accountingLabel,
      taxLabel,
      showCategoriesSettings,
      isLegalEntityRootOrg,
      isStableCoinsEnabled,
      isAuditLogEnabled,
      hasPermission,
      user?.isSuperAdmin,
      legalEntitiesLabel,
      enableCustomFields,
      isMultiBookEnabled,
      user?.isZeusITAdmin,
      isAccountingPageVisible,
      isCustomErpMappingsEnabled,
    ]
  )
}
